import { useEffect, useEffectEvent } from 'react'

/**
 * Minimal shared hotkey hook. The app has no central keyboard system — every
 * feature hand-rolls `addEventListener('keydown', …)` with its own input-gating.
 * This consolidates that: a combo string, input-gating by default, and `mod` as
 * the platform primary (Cmd on macOS, Ctrl elsewhere).
 *
 * Combo grammar: `'+'`-separated tokens, case-insensitive. Modifiers:
 * `mod` (meta OR ctrl), `ctrl`, `meta`/`cmd`, `shift`, `alt`/`option`. The final
 * non-modifier token is the key (matched against `event.key`, lowercased),
 * e.g. `'mod+b'`, `'mod+1'`, `'mod+shift+['`.
 */
export interface HotkeyOptions {
  /** Fire even when focus is in an input/textarea/select/contenteditable. Default false. */
  allowInInput?: boolean
  /** Disable the binding without unmounting the caller. Default true. */
  enabled?: boolean
}

export interface ParsedHotkey {
  mod: boolean
  ctrl: boolean
  meta: boolean
  shift: boolean
  alt: boolean
  key: string
}

export function parseHotkey(combo: string): ParsedHotkey {
  const parsed: ParsedHotkey = {
    mod: false,
    ctrl: false,
    meta: false,
    shift: false,
    alt: false,
    key: '',
  }
  for (const raw of combo.toLowerCase().split('+')) {
    const t = raw.trim()
    if (!t) continue
    if (t === 'mod') parsed.mod = true
    else if (t === 'ctrl' || t === 'control') parsed.ctrl = true
    else if (t === 'meta' || t === 'cmd' || t === 'command') parsed.meta = true
    else if (t === 'shift') parsed.shift = true
    else if (t === 'alt' || t === 'option') parsed.alt = true
    else parsed.key = t
  }
  return parsed
}

export function matchesHotkey(e: KeyboardEvent, combo: string): boolean {
  const p = parseHotkey(combo)
  if (e.key.toLowerCase() !== p.key) return false
  if (p.mod && !(e.metaKey || e.ctrlKey)) return false
  if (p.ctrl && !e.ctrlKey) return false
  if (p.meta && !e.metaKey) return false
  if (p.shift !== e.shiftKey) return false
  if (p.alt !== e.altKey) return false
  // A bare-key binding must NOT fire while a command modifier is held (so
  // `b` doesn't trigger on Cmd+B). Only enforced when no command mod is required.
  if (!p.mod && !p.ctrl && !p.meta && (e.metaKey || e.ctrlKey)) return false
  return true
}

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  // `isContentEditable` is unimplemented in jsdom (returns undefined), so fall
  // back to the attribute — real browsers honour the property.
  return target.isContentEditable === true || target.getAttribute('contenteditable') === 'true'
}

export function useHotkey(
  combo: string,
  handler: (e: KeyboardEvent) => void,
  opts: HotkeyOptions = {},
): void {
  const { allowInInput = false, enabled = true } = opts
  // The latest handler is used without re-binding the listener each render.
  const onHotkey = useEffectEvent(handler)

  useEffect(() => {
    if (!enabled) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (!allowInInput && isEditableTarget(e.target)) return
      if (!matchesHotkey(e, combo)) return
      e.preventDefault()
      onHotkey(e)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [combo, allowInInput, enabled])
}
